// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { Avatar, Box, IconButton, InputLabel, Portal, TextField, Typography } from '@mui/material';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { forwardRef, InputHTMLAttributes, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { DateFormatEnum } from '~/utils/enum/common';
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseDatePicker.module.scss';
import { icons } from '~/assets';
import 'react-datepicker/dist/react-datepicker.css';

type Props = {
  label?: string;
  value?: Date | string;
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  height?: number | string;
  width?: number | string;
  maxDate?: Date;
  minDate?: Date;
  onChange?: (date: string) => void;
};

const cx = classNames.bind(styles);

type TextFieldInputProps = InputHTMLAttributes<HTMLInputElement>;

const BaseDatePicker = (props: Props) => {
  //#region Destructuring Props
  const {
    label,
    value,
    errorMessage,
    disabled = false,
    required = false,
    height = 35,
    width,
    minDate,
    maxDate,
    onChange,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateText, setDateText] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!value) {
      setSelectedDate(null);
      setDateText(EMPTY_STRING);
      return;
    }

    const date = moment(value).toDate();
    const dateString = moment(value).format(DateFormatEnum.DD_MMM_YYYY);
    setDateText(dateString);
    setSelectedDate(date);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleChange = (date: Date | null) => {
    if (!date) return;

    setSelectedDate(date);
    onChange && onChange(moment(date).format(DateFormatEnum.YYYY_MM_DD));
    setDateText(moment(date).format(DateFormatEnum.DD_MMM_YYYY));
  };

  const TextFieldInput = forwardRef<HTMLInputElement, TextFieldInputProps>(({ onClick }, ref) => (
    <TextField
      fullWidth
      ref={ref}
      classes={{ root: cx('textField') }}
      sx={{}}
      slotProps={{
        input: {
          className: cx('textFieldInput', { disabled: disabled }),
          sx: {
            height,
            width,
          },
          readOnly: true,
          endAdornment: <Avatar src={icons.commonIconArrowDown} className={cx('selectArrow')} />,
        },
        formHelperText: { className: cx('errorMessage') },
      }}
      disabled={disabled}
      onClick={onClick}
      value={dateText}
      placeholder={t('common_select_placeholder')}
      helperText={errorMessage}
    />
  ));

  const renderCustomHeader = ({
    monthDate,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: ReactDatePickerCustomHeaderProps) => (
    <Box className={cx('header')}>
      <IconButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled} className={cx('iconButton')}>
        <img src={icons.paginationIconPreviousActive} alt={t('common_alt_img')} className={cx('icon')} />
      </IconButton>
      <Typography className={cx('titleHeader')}>
        {moment(monthDate).format(DateFormatEnum.MMMM_YYYY)}
      </Typography>
      <IconButton onClick={increaseMonth} disabled={nextMonthButtonDisabled} className={cx('iconButton')}>
        <img src={icons.paginationIconNextActive} alt={t('common_alt_img')} className={cx('icon')} />
      </IconButton>
    </Box>
  );
  //#endregion Handle Function

  return (
    <Box className={cx('container')} id='baseDatePicker'>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <Box className={cx('content')}>
        <DatePicker
          portalId='baseDatePicker'
          popperContainer={Portal}
          popperPlacement='bottom-start'
          onChange={handleChange}
          selected={selectedDate}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={DateFormatEnum.DD_MMM_YYYY}
          showPopperArrow={false}
          renderCustomHeader={(params) => renderCustomHeader({ ...params })}
          customInput={<TextFieldInput />}
        />
      </Box>
    </Box>
  );
};

export default memo(BaseDatePicker);
